<template>
  <div>
    <!-- Snackbar แจ้งเตือนเมื่อพบ version ใหม่ -->
    <v-snackbar
      v-model="showSnackbar"
      :timeout="-1"
      bottom
      right
      color="primary"
      max-width="420"
      min-width="340"
    >
      <div class="d-flex align-center">
        <v-icon left color="white" class="mr-3 pulse-icon">mdi-rocket-launch</v-icon>
        <div>
          <div class="font-weight-bold white--text" style="font-size: 14px;">
            มีเวอร์ชันใหม่พร้อมใช้งาน!
          </div>
          <div class="white--text mt-1" style="font-size: 12px; opacity: 0.9;">
            {{ currentVersion }} → {{ latestVersion }}
          </div>
          <div v-if="latestBuildDate" class="white--text" style="font-size: 11px; opacity: 0.7;">
            อัปเดทเมื่อ: {{ formatDate(latestBuildDate) }}
          </div>
        </div>
      </div>

      <template v-slot:action="{ attrs }">
        <v-btn text small color="white" v-bind="attrs" @click="dismissUpdate" class="mr-1">
          ภายหลัง
        </v-btn>
        <v-btn color="white" small outlined v-bind="attrs" @click="showDetailDialog = true">
          <v-icon left small>mdi-information-outline</v-icon>
          รายละเอียด
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Dialog รายละเอียดและยืนยันอัปเดท -->
    <v-dialog v-model="showDetailDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="primary white--text py-3">
          <v-icon left color="white">mdi-update</v-icon>
          อัปเดทเวอร์ชันใหม่
        </v-card-title>

        <v-card-text class="pt-5 pb-2">
          <div class="text-center mb-5">
            <div class="d-flex align-center justify-center">
              <div class="version-box current-version">
                <div class="version-label">เวอร์ชันปัจจุบัน</div>
                <div class="version-number">{{ currentVersion }}</div>
              </div>
              <v-icon color="primary" class="mx-4" size="28">mdi-arrow-right-bold</v-icon>
              <div class="version-box new-version">
                <div class="version-label">เวอร์ชันใหม่</div>
                <div class="version-number">{{ latestVersion }}</div>
              </div>
            </div>
          </div>

          <v-list dense class="pa-0">
            <v-list-item v-if="latestBuildDate">
              <v-list-item-icon class="mr-3">
                <v-icon color="grey darken-1" small>mdi-calendar-clock</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>วันที่อัปเดท</v-list-item-subtitle>
                <v-list-item-title>{{ formatDate(latestBuildDate) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="lastChecked">
              <v-list-item-icon class="mr-3">
                <v-icon color="grey darken-1" small>mdi-clock-check-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>ตรวจสอบล่าสุด</v-list-item-subtitle>
                <v-list-item-title>{{ formatTime(lastChecked) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-divider class="my-3"></v-divider>

          <v-alert type="info" dense text class="mb-0" style="font-size: 13px;">
            <strong>หมายเหตุ:</strong> ระบบจะทำการล้างแคชและโหลดหน้าเว็บใหม่
            กรุณาบันทึกข้อมูลก่อนกดอัปเดท
          </v-alert>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn text color="grey darken-1" @click="dismissUpdate">
            <v-icon left small>mdi-clock-outline</v-icon>
            อัปเดทภายหลัง
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" :loading="updating" :disabled="updating" @click="confirmUpdate">
            <v-icon left>mdi-download</v-icon>
            อัปเดทเลย
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Floating badge (แสดงหลังจาก dismiss snackbar) -->
    <v-tooltip left v-if="showFloatingBadge">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          fab small color="primary" fixed bottom right
          class="mb-8 mr-2 floating-update-btn"
          v-bind="attrs" v-on="on"
          @click="showDetailDialog = true"
        >
          <v-badge color="red" dot overlap>
            <v-icon>mdi-update</v-icon>
          </v-badge>
        </v-btn>
      </template>
      <span>มีเวอร์ชันใหม่: {{ latestVersion }}</span>
    </v-tooltip>
  </div>
</template>

<script>
/**
 * VersionUpdateNotifier
 *
 * ✅ ไม่ต้องพึ่ง Vuex module "version/" เลย
 * ✅ ใช้ $appVersion จาก version-plugin.js (ที่ทำงานอยู่แล้ว)
 * ✅ Fetch /version.json ตรงๆ ใน component
 *
 * วิธีใช้: เพิ่ม <VersionUpdateNotifier /> ใน App.vue
 */
export default {
  name: 'VersionUpdateNotifier',

  data() {
    return {
      showSnackbar: false,
      showDetailDialog: false,
      showFloatingBadge: false,
      updating: false,
      dismissed: false,
      checkInterval: null,

      // Version state (จัดการเองใน component — ไม่ใช้ Vuex)
      currentVersion: '',
      latestVersion: '',
      latestBuildDate: null,
      lastChecked: null,
      hasUpdate: false
    }
  },

  mounted() {
    this.initVersion()
  },

  beforeDestroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },

  methods: {
    /**
     * Initialize: อ่าน current version จาก $appVersion (version-plugin)
     * แล้วเริ่ม check loop
     */
    async initVersion() {
      // รอให้ version-plugin โหลด version.json เสร็จก่อน
      await this.$nextTick()

      // ดึง current version จาก $appVersion ที่ version-plugin.js โหลดมาแล้ว
      if (this.$appVersion && this.$appVersion.version && this.$appVersion.version !== '1.0.0') {
        this.currentVersion = this.$appVersion.version
      } else {
        // Fallback: fetch เอง
        try {
          const res = await fetch('/version.json?t=' + Date.now())
          const data = await res.json()
          this.currentVersion = data.version || '1.0.0'
        } catch (err) {
          this.currentVersion = '1.0.0'
        }
      }

      // ใช้ localStorage เก็บ version ที่ user ใช้อยู่จริง
      // ป้องกัน: หลัง deploy ใหม่ → version.json เปลี่ยน → $appVersion โหลดค่าใหม่มา
      // ทำให้ current = latest → ไม่แจ้งเตือน (false negative)
      const savedVersion = localStorage.getItem('app_current_version')
      if (savedVersion) {
        this.currentVersion = savedVersion
      } else {
        localStorage.setItem('app_current_version', this.currentVersion)
      }

      console.log('📦 VersionUpdateNotifier initialized, current:', this.currentVersion)

      // Check ครั้งแรกหลัง 5 วินาที
      setTimeout(() => {
        this.checkForUpdate()
      }, 5000)

      // Check ทุก 5 นาที
      this.checkInterval = setInterval(() => {
        this.checkForUpdate()
      }, 5 * 60 * 1000)

      // Check เมื่อ user กลับมาที่ tab
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    },

    /**
     * Fetch /version.json แล้วเปรียบเทียบกับ currentVersion
     */
    async checkForUpdate() {
      try {
        const res = await fetch('/version.json?t=' + Date.now())

        if (!res.ok) {
          throw new Error('HTTP ' + res.status)
        }

        const contentType = res.headers.get('content-type')
        if (contentType && !contentType.includes('application/json')) {
          throw new Error('Invalid content type')
        }

        const data = await res.json()

        if (!data.version) {
          throw new Error('No version in response')
        }

        this.latestVersion = data.version
        this.latestBuildDate = data.buildDate || null
        this.lastChecked = new Date().toISOString()

        // เปรียบเทียบ
        this.hasUpdate = this.currentVersion !== data.version

        if (this.hasUpdate && !this.dismissed) {
          this.showSnackbar = true
          this.showFloatingBadge = false
          console.log('🚀 Update available:', this.currentVersion, '→', data.version)
        } else if (!this.hasUpdate) {
          console.log('✅ Version up to date:', this.currentVersion)
        }
      } catch (err) {
        console.error('Version check failed:', err)
      }
    },

    onVisibilityChange() {
      if (!document.hidden) {
        this.checkForUpdate()
      }
    },

    dismissUpdate() {
      this.showSnackbar = false
      this.showDetailDialog = false
      this.dismissed = true

      // แสดง floating badge แทน
      if (this.hasUpdate) {
        this.showFloatingBadge = true
      }

      // Reset dismissed หลัง 30 นาที เพื่อแจ้งเตือนอีกครั้ง
      setTimeout(() => {
        this.dismissed = false
        if (this.hasUpdate) {
          this.showSnackbar = true
          this.showFloatingBadge = false
        }
      }, 30 * 60 * 1000)
    },

    async confirmUpdate() {
      this.updating = true

      try {
        // อัปเดท localStorage → หลัง reload จะรู้ว่า current = version ใหม่
        localStorage.setItem('app_current_version', this.latestVersion)

        // ล้าง Cache API
        if ('caches' in window) {
          const cacheNames = await caches.keys()
          await Promise.all(cacheNames.map(name => caches.delete(name)))
          console.log('✅ Cleared', cacheNames.length, 'caches')
        }

        // Unregister Service Workers
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations()
          await Promise.all(registrations.map(reg => reg.unregister()))
          console.log('✅ Unregistered', registrations.length, 'service workers')
        }

        // Force reload
        window.location.reload(true)
      } catch (err) {
        console.error('Update failed:', err)
        this.updating = false
        window.location.reload(true)
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return 'ไม่มีข้อมูล'
      try {
        return new Date(dateStr).toLocaleDateString('th-TH', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch {
        return 'ไม่สามารถแสดงได้'
      }
    },

    formatTime(dateStr) {
      if (!dateStr) return ''
      try {
        return new Date(dateStr).toLocaleTimeString('th-TH', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      } catch {
        return ''
      }
    }
  }
}
</script>

<style scoped>
.version-box {
  padding: 12px 20px;
  border-radius: 8px;
  text-align: center;
  min-width: 120px;
}

.current-version {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
}

.new-version {
  background: #e3f2fd;
  border: 1px solid #90caf9;
}

.version-label {
  font-size: 11px;
  color: #757575;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.version-number {
  font-size: 20px;
  font-weight: 700;
  color: #333;
}

.new-version .version-number {
  color: #1565c0;
}

.pulse-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

.floating-update-btn {
  z-index: 5;
  animation: bounce 2s ease infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-8px); }
  60% { transform: translateY(-4px); }
}
</style>